import { useEffect, useState } from 'react';
import {
  UNIT_STATUS_LABELS, SCREENING_STATUS_LABELS, ANTICOAGULANT_LABELS,
  BLOOD_GROUPS, RH_FACTORS, canTransition,
  type Paginated, type BloodGroup, type RhFactor, type Anticoagulant,
  type UnitStatus, type ScreeningStatus, type ScreeningMarker, type SplitResult,
  MAX_PAGE_SIZE,
} from 'shared';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../context/AuthContext';

interface Named { id: string; code: string; name: string }
interface DonorRow { id: string; name: string; nikLast4: string; abo: string | null; rh: string | null }
interface DonationRow {
  id: string; donationNumber: string; collectedAt: string; volumeMl: number;
  abo: string; rh: string; screeningStatus: ScreeningStatus;
  donorName: string; donorId: string; branchCode: string;
}
interface UnitRow {
  id: string; unitNumber: string; componentCode: string; status: UnitStatus;
  screeningStatus: ScreeningStatus; volumeMl: number;
  expiresAt: string; effectiveExpiresAt: string | null; parentUnitId: string | null;
}

const MARKERS = ['hiv', 'hbsag', 'hcv', 'syphilis', 'malaria'] as const;
const MARKER_LABELS: Record<(typeof MARKERS)[number], string> = {
  hiv: 'HIV', hbsag: 'HBsAg', hcv: 'HCV', syphilis: 'Sifilis', malaria: 'Malaria',
};
const CLEAR_PANEL = {
  hiv: 'non_reactive', hbsag: 'non_reactive', hcv: 'non_reactive',
  syphilis: 'non_reactive', malaria: 'not_tested',
} as Record<string, ScreeningMarker>;

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('id-ID',
  { day: '2-digit', month: 'short', year: 'numeric' });

export function Donations() {
  const { can } = useAuth();
  const [rows, setRows] = useState<DonationRow[]>([]);
  const [donors, setDonors] = useState<DonorRow[]>([]);
  const [branches, setBranches] = useState<Named[]>([]);
  const [components, setComponents] = useState<{ id: string; code: string; name: string; class: string }[]>([]);
  const [selected, setSelected] = useState<DonationRow | null>(null);
  const [units, setUnits] = useState<UnitRow[]>([]);
  const [panel, setPanel] = useState<Record<string, ScreeningMarker>>(CLEAR_PANEL);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [warn, setWarn] = useState<string | null>(null);

  const [form, setForm] = useState({
    donorId: '', branchId: '', volumeMl: '450',
    anticoagulant: 'CPDA_1' as Anticoagulant, abo: 'O' as BloodGroup, rh: 'positive' as RhFactor,
  });

  async function loadList() {
    try {
      setRows((await api.get<Paginated<DonationRow>>('/donations?pageSize=50')).items);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Gagal memuat donasi.');
    }
  }

  useEffect(() => {
    (async () => {
      const [b, d, c] = await Promise.all([
        api.get<Paginated<Named>>('/branches?pageSize=100').catch(() => ({ items: [] as Named[] })),
        api.get<Paginated<DonorRow>>('/donors?pageSize=100').catch(() => ({ items: [] as DonorRow[] })),
        api.get<Paginated<{ id: string; code: string; name: string; class: string }>>(
          `/blood/components?pageSize=${MAX_PAGE_SIZE}`,
        ).catch(() => ({ items: [] })),
      ]);
      setBranches(b.items); setDonors(d.items); setComponents(c.items);
      if (b.items[0]) setForm((f) => ({ ...f, branchId: b.items[0]!.id }));
      if (d.items[0]) setForm((f) => ({ ...f, donorId: d.items[0]!.id }));
    })();
    void loadList();
  }, []);

  async function openDonation(row: DonationRow) {
    setSelected(row); setNotice(null); setWarn(null);
    setUnits(await api.get<UnitRow[]>(`/donations/${row.id}/units`));
  }

  async function recordDonation(e: React.FormEvent) {
    e.preventDefault();
    setError(null); setNotice(null);
    try {
      const r = await api.post<{ donationNumber: string; unit: { unitNumber: string } }>('/donations', {
        donorId: form.donorId, branchId: form.branchId,
        collectedAt: new Date().toISOString(),
        volumeMl: Number(form.volumeMl),
        anticoagulant: form.anticoagulant, abo: form.abo, rh: form.rh,
        initialComponentCode: 'WB',
      });
      setNotice(`Donasi ${r.donationNumber} tercatat. Kantong ${r.unit.unitNumber} menunggu skrining.`);
      await loadList();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Gagal mencatat donasi.');
    }
  }

  async function recordScreening() {
    if (!selected) return;
    setError(null); setNotice(null); setWarn(null);
    try {
      const r = await api.post<{ verdict: ScreeningStatus; message: string; donorDeferred: boolean }>(
        `/donations/${selected.id}/screening`, panel,
      );
      if (r.verdict === 'reactive') setWarn(r.message); else setNotice(r.message);
      await loadList();
      await openDonation(selected);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Gagal menyimpan skrining.');
    }
  }

  async function move(unitId: string, to: UnitStatus) {
    setError(null);
    try {
      const body: Record<string, unknown> = { to };
      if (to === 'discarded') {
        const reason = prompt('Alasan pemusnahan:');
        if (!reason) return;
        body.discardReason = reason;
      }
      await api.post(`/units/${unitId}/transition`, body);
      if (selected) await openDonation(selected);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Gagal mengubah status.');
    }
  }

  async function splitUnit(unitId: string) {
    setError(null); setNotice(null);
    try {
      const r = await api.post<SplitResult>(`/units/${unitId}/split`, {
        components: [
          { componentCode: 'PRC', volumeMl: 250, count: 1 },
          { componentCode: 'TC', volumeMl: 50, count: 1 },
          { componentCode: 'FFP', volumeMl: 150, count: 1 },
        ],
      });
      setNotice(
        `${r.parentUnitNumber} dipecah menjadi ${r.children.length} komponen dengan `
        + 'tanggal kadaluarsa masing-masing.',
      );
      if (selected) await openDonation(selected);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Gagal memecah kantong.');
    }
  }

  const canSplit = components.length > 0;

  return (
    <div className="stack">
      <div>
        <h2>Donasi &amp; Pengolahan</h2>
        <p className="muted" style={{ margin: '0.3rem 0 0', fontSize: 14 }}>
          Kantong tidak bisa masuk stok sebelum skrining selesai dan non-reaktif —
          itu ditegakkan basis data, bukan tampilan ini.
        </p>
      </div>

      {error && <div className="msg error">{error}</div>}
      {notice && <div className="msg ok">{notice}</div>}
      {warn && (
        <div className="msg error" style={{ borderWidth: 2 }}>
          <strong>Hasil reaktif.</strong> {warn}
        </div>
      )}

      {can('donation:write') && (
        <form onSubmit={recordDonation} className="card stack">
          <h3>Catat donasi</h3>
          <div className="grid-2">
            <div className="field">
              <label className="label" htmlFor="d-donor">Pendonor</label>
              <select id="d-donor" value={form.donorId} required
                onChange={(e) => setForm({ ...form, donorId: e.target.value })}>
                {donors.length === 0 && <option value="">— belum ada pendonor —</option>}
                {donors.map((d) => (
                  <option key={d.id} value={d.id}>{d.name} · NIK …{d.nikLast4}</option>
                ))}
              </select>
            </div>
            <div className="field">
              <label className="label" htmlFor="d-branch">Cabang</label>
              <select id="d-branch" value={form.branchId} required
                onChange={(e) => setForm({ ...form, branchId: e.target.value })}>
                {branches.map((b) => <option key={b.id} value={b.id}>{b.code}</option>)}
              </select>
            </div>
            <div className="field">
              <label className="label" htmlFor="d-vol">Volume (ml)</label>
              <input id="d-vol" value={form.volumeMl} inputMode="numeric" required
                onChange={(e) => setForm({ ...form, volumeMl: e.target.value })} />
            </div>
            <div className="field">
              <label className="label" htmlFor="d-ac">Antikoagulan</label>
              <select id="d-ac" value={form.anticoagulant}
                onChange={(e) => setForm({ ...form, anticoagulant: e.target.value as Anticoagulant })}>
                {(Object.keys(ANTICOAGULANT_LABELS) as Anticoagulant[]).map((a) => (
                  <option key={a} value={a}>{ANTICOAGULANT_LABELS[a]}</option>
                ))}
              </select>
            </div>
            <div className="field">
              <label className="label" htmlFor="d-abo">Golongan (hasil lab)</label>
              <select id="d-abo" value={form.abo}
                onChange={(e) => setForm({ ...form, abo: e.target.value as BloodGroup })}>
                {BLOOD_GROUPS.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div className="field">
              <label className="label" htmlFor="d-rh">Rhesus</label>
              <select id="d-rh" value={form.rh}
                onChange={(e) => setForm({ ...form, rh: e.target.value as RhFactor })}>
                {RH_FACTORS.map((f) => (
                  <option key={f} value={f}>{f === 'positive' ? 'Positif' : 'Negatif'}</option>
                ))}
              </select>
            </div>
          </div>
          <div><button type="submit" className="primary" disabled={donors.length === 0}>Catat donasi</button></div>
        </form>
      )}

      <div className="card">
        <h3 style={{ marginBottom: '0.7rem' }}>Donasi terbaru</h3>
        {rows.length === 0 ? <p className="muted">Belum ada donasi.</p> : (
          <table>
            <thead>
              <tr><th>Nomor</th><th>Pendonor</th><th>Golongan</th><th>Diambil</th><th>Skrining</th><th></th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="mono" style={{ fontSize: 12.5 }}>{r.donationNumber}</td>
                  <td>{r.donorName}</td>
                  <td className="mono">{r.abo}{r.rh === 'positive' ? '+' : '−'}</td>
                  <td className="muted" style={{ fontSize: 13 }}>{fmtDate(r.collectedAt)}</td>
                  <td>
                    <span className={r.screeningStatus === 'non_reactive' ? 'pill' : 'pill accent'}>
                      {SCREENING_STATUS_LABELS[r.screeningStatus]}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button style={{ padding: '0.25rem 0.7rem', fontSize: 13 }}
                      onClick={() => void openDonation(r)}>Buka</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selected && (
        <div className="card stack">
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <h3>{selected.donationNumber}</h3>
            <button style={{ padding: '0.25rem 0.7rem', fontSize: 13 }}
              onClick={() => { setSelected(null); setUnits([]); }}>Tutup</button>
          </div>

          {can('screening:write') && (
            <div className="stack" style={{ gap: '0.6rem' }}>
              <span className="label">Panel skrining serologi</span>
              <div className="grid-2">
                {MARKERS.map((m) => (
                  <div className="field" key={m}>
                    <label className="label" htmlFor={`m-${m}`}>{MARKER_LABELS[m]}</label>
                    <select id={`m-${m}`} value={panel[m]}
                      onChange={(e) => setPanel({ ...panel, [m]: e.target.value as ScreeningMarker })}>
                      <option value="not_tested">Belum diperiksa</option>
                      <option value="non_reactive">Non-reaktif</option>
                      <option value="reactive">Reaktif</option>
                    </select>
                  </div>
                ))}
              </div>
              <p className="muted" style={{ fontSize: 12.5, margin: 0 }}>
                Panel wajib: HIV, HBsAg, HCV, sifilis. Malaria hanya untuk wilayah
                endemis. Satu penanda reaktif membuat keseluruhannya reaktif.
              </p>
              <div><button className="primary" onClick={recordScreening}>Simpan hasil skrining</button></div>
            </div>
          )}

          <div>
            <span className="label">Kantong dari donasi ini</span>
            <table style={{ marginTop: '0.5rem' }}>
              <thead>
                <tr><th>Nomor</th><th>Komponen</th><th>Vol</th><th>Kadaluarsa</th><th>Status</th><th></th></tr>
              </thead>
              <tbody>
                {units.map((u) => (
                  <tr key={u.id}>
                    <td className="mono" style={{ fontSize: 12 }}>
                      {u.unitNumber}
                      {u.parentUnitId && <span className="pill" style={{ marginLeft: 5 }}>turunan</span>}
                    </td>
                    <td className="mono">{u.componentCode}</td>
                    <td className="mono muted">{u.volumeMl}</td>
                    <td style={{ fontSize: 12.5 }}>{fmtDate(u.effectiveExpiresAt ?? u.expiresAt)}</td>
                    <td>
                      <span className={u.status === 'available' ? 'pill accent' : 'pill'}>
                        {UNIT_STATUS_LABELS[u.status]}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {can('stock:write') && (
                        <>
                          {(['screening', 'released', 'available'] as UnitStatus[])
                            .filter((to) => canTransition(u.status, to))
                            .map((to) => (
                              <button key={to} style={{ padding: '0.2rem 0.55rem', fontSize: 12, marginLeft: 4 }}
                                onClick={() => void move(u.id, to)}>
                                → {UNIT_STATUS_LABELS[to]}
                              </button>
                            ))}
                          {u.componentCode === 'WB' && canTransition(u.status, 'processed') && canSplit && (
                            <button style={{ padding: '0.2rem 0.55rem', fontSize: 12, marginLeft: 4 }}
                              onClick={() => void splitUnit(u.id)}>Pecah komponen</button>
                          )}
                          {canTransition(u.status, 'discarded') && (
                            <button className="danger" style={{ padding: '0.2rem 0.55rem', fontSize: 12, marginLeft: 4 }}
                              onClick={() => void move(u.id, 'discarded')}>Musnahkan</button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
